import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Field, Form, Formik } from 'formik';
import { get } from 'lodash';
import { Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { object, string } from 'yup';
import { useTranslation } from '../../src/i18n';
import SubmitModalLayout from '../SubmitModalLayout';
import { TP } from '../../constants/consts';
import EditModeCard from '../EditModeCard';
import FormikTextField from '../Fields/TextField';
import { createEntity, useBufferValueAction } from '../../containers/App/actions';
import FormikAutocompleteSelect from '../Fields/FormikAutocompleteSelect';
import { CITY_ENDPOINT } from '../../constants/endpointConstants';
import { selectBufferValueData } from '../../containers/App/selectors';

const useStyles = makeStyles(theme => ({
  form: {
    margin: theme.spacing(2),
  },
  fieldsBox: {
    padding: theme.spacing(2),
  },
  scrollable: {
    minHeight: '60vh',
  },
  select: {
    maxWidth: '360px',
  },
}));

const countryFields = {
  name: {
    name: 'name',
    label: `${TP}.m_CITY`,
  },
  country: {
    name: 'country',
    label: `${TP}.m_COUNTRY`,
  },
};

const { name, country } = countryFields;

const AddNewCityModal = ({ onClose, countriesOptions, countryValue, cityValue }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formRef = React.createRef(null);
  const createdCityData = useSelector(selectBufferValueData());

  const initialValues = {
    [name.name]: cityValue,
    [country.name]: countryValue,
  };

  const createCity = values => {
    dispatch(
      createEntity({
        endpoint: CITY_ENDPOINT,
        sagaRoutine: useBufferValueAction,
        params: {
          name: get(values, name.name),
          country: { id: get(values, country.name)?.value },
        },
        successMessage: t(`${TP}.SUCCESS_CREATING_CITY`),
        callback: createdCity => {
          onClose(createdCity);
        },
      }),
    );
  };

  return (
    <SubmitModalLayout
      isOpened
      onClose={onClose}
      title1={t(`${TP}.FN_ADD_NEW_CITY`)}
      submitFunc={() => {
        get(formRef, 'current.submitForm', () => {})();
      }}
      className={classes.scrollable}
      isLoading={createdCityData?.loading}
    >
      <EditModeCard className={classes.form}>
        <Formik
          initialValues={initialValues}
          onSubmit={createCity}
          innerRef={formRef}
          validationSchema={AddNewCitySchema}
        >
          {() => (
            <Form className={classes.scrollable}>
              <Grid container spacing={4} className={classes.fieldsBox} alignItems="flex-start">
                <Grid item lg={5} sm={6} xs={12} classes={{ root: classes.select }}>
                  <Field name={name.name} component={FormikTextField} label={t(name.label)} />
                </Grid>
                <Grid item lg={5} sm={6} xs={12} classes={{ root: classes.select }}>
                  <Field
                    name={country.name}
                    component={FormikAutocompleteSelect}
                    label={t(country.label)}
                    options={countriesOptions}
                    withFiltering
                    instanceId={country.name}
                    isClearable
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </EditModeCard>
    </SubmitModalLayout>
  );
};

AddNewCityModal.propTypes = {
  onClose: PropTypes.func,
  countriesOptions: PropTypes.array,
  countryValue: PropTypes.object,
  cityValue: PropTypes.string,
};

export default AddNewCityModal;

const AddNewCitySchema = () =>
  object().shape({
    [name.name]: string()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [country.name]: object()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
  });
